/**
 * Agent-context file scaffolding written by the CLI.
 *
 * One layer, one job: per-hub `wayai-ws/hubs/<hub>/AGENTS.md` (+ a `CLAUDE.md`
 * shim) — hub-specific MEMORY, the context the config files (hub.yaml, agents/)
 * can't capture. A scaffold the agent fills in, seeded by `writeHubFolder`
 * (yaml-writer.ts) via `perHubAgentsMd`.
 *
 * The CLI does NOT seed a repo-root `AGENTS.md`/`CLAUDE.md`. Every supported
 * harness takes its instructions from the WayAI skill, which is the sole entry
 * point — a generic root bootstrap only restated what the skill already says and
 * went stale between releases.
 *
 * Every write is absence-guarded — a user's own file is NEVER overwritten.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';

/** One-line Claude Code shim that imports the sibling AGENTS.md. */
export const CLAUDE_MD_SHIM = '@AGENTS.md\n';

/**
 * Scaffold for a hub folder's `AGENTS.md` — hub-specific memory the agent fills
 * in. `hubFolderName` titles it (e.g. "support--abc123").
 */
export function perHubAgentsMd(hubFolderName: string): string {
  return [
    `# ${hubFolderName}`,
    '',
    "Notes about THIS hub that the config files (hub.yaml, agents/) don't capture:",
    'why it exists, key decisions and the reasons behind them, business rules that',
    'only apply here, terminology, integration quirks.',
    '',
    'Keep it short and about *why / what is different* — platform mechanics live in',
    'the WayAI skill. Fill this in as you learn about the hub; delete this',
    'placeholder once you do.',
    '',
  ].join('\n');
}

/** Absence-guarded write. Returns the basename if written, null if it existed. */
export function writeIfAbsent(filePath: string, content: string): string | null {
  if (fs.existsSync(filePath)) return null;
  fs.writeFileSync(filePath, content, 'utf-8');
  return path.basename(filePath);
}
